import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type { ProductAlertSettings, StockAlertRequest, StockAlertSummary } from '../lib/types';

/**
 * Pazarlama → Stok ve fiyat bildirimleri (F6-08, ADR-089): vitrinde "Gelince haber ver", favorilerde fiyat düşüşü
 * bildirimi (ikisi de varsayılan kapalı), varyant başına bekleyen talepler, talep listesi ve iptal.
 */

const PERCENTS = [5, 10, 15, 20, 25, 30, 40, 50];

export function ProductAlertsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const summary = useQuery({
    queryKey: ['stock-alerts'],
    queryFn: () => api.get<StockAlertSummary>('/v1/admin/stock-alerts'),
  });
  const [open, setOpen] = useState<StockAlertSummary['variants'][number] | null>(null);
  return (
    <PageFrame helpKey="page.product_alerts">
      <div className="space-y-8">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.productAlerts.lead')}</p>
        <SettingsSection />
        <Section
          title={t('admin.productAlerts.requestsTitle')}
          description={t('admin.productAlerts.requestsLead')}
          action={<HelpTip k="product_alerts.requests" />}
        >
          {!summary.data ? (
            <Spinner label={t('common.loading')} />
          ) : !summary.data.variants.length ? (
            <EmptyState
              title={t('admin.productAlerts.emptyTitle')}
              description={t('admin.productAlerts.emptyLead')}
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.productAlerts.requestsTitle')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('admin.productAlerts.colProduct')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.productAlerts.colWaiting')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.productAlerts.colNotified')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.productAlerts.colStock')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.productAlerts.colOldest')}</th>
                    <th className="px-3 py-2">
                      <span className="sr-only">{t('admin.productAlerts.colActions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.data.variants.map((r, i) => (
                    <tr key={r.variantId} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <span className="font-medium">{r.productTitle}</span>
                        <span className="block text-ink-3">
                          {[r.variantTitle, r.sku].filter(Boolean).join(' · ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">{r.waiting}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{r.notified}</td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {r.available > 0 ? (
                          <Badge tone="moss">
                            {t('admin.productAlerts.inStock', { count: r.available })}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">{t('admin.productAlerts.outOfStock')}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-2">
                        {r.oldestWaitingAt ? formatDate(r.oldestWaitingAt) : '—'}
                      </td>
                      <td className="px-3 py-2 text-end">
                        <span className="inline-flex items-center gap-1">
                          <Button
                            size="sm"
                            onClick={() => setOpen(r)}
                            aria-label={t('admin.productAlerts.viewFor', { product: r.productTitle })}
                          >
                            {t('admin.productAlerts.view')}
                          </Button>
                          {i === 0 && <HelpTip k="product_alerts.view" />}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {summary.data && (
            <p className="mt-3 text-sm text-ink-3">
              {t('admin.productAlerts.priceDropLast30', {
                sent: summary.data.priceDropLast30.sent,
                skipped: summary.data.priceDropLast30.skipped,
              })}
            </p>
          )}
        </Section>
      </div>
      {open && <RequestsDialog row={open} onClose={() => setOpen(null)} />}
    </PageFrame>
  );
}

function SettingsSection() {
  const t = useT();
  const { can } = usePermissions();
  const canManage = can('automation.manage');
  const { stores } = useStoreChoice();
  const q = useQuery({
    queryKey: ['product-alert-settings'],
    queryFn: () => api.get<ProductAlertSettings>('/v1/admin/product-alert-settings'),
  });
  const [draft, setDraft] = useState<ProductAlertSettings | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);
  const save = useApiMutation(
    () => api.put<ProductAlertSettings>('/v1/admin/product-alert-settings', draft),
    {
      invalidate: [['product-alert-settings'], ['stock-alerts']],
      success: t('admin.productAlerts.saved'),
    },
  );
  if (!draft || !q.data) return <Spinner label={t('common.loading')} />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data);
  const turningOn =
    (draft.stockAlerts && !q.data.stockAlerts) || (draft.priceDrop.enabled && !q.data.priceDrop.enabled);
  const pd = draft.priceDrop;
  const setPd = (patch: Partial<ProductAlertSettings['priceDrop']>) =>
    setDraft({ ...draft, priceDrop: { ...pd, ...patch } });
  return (
    <Section
      title={t('admin.productAlerts.settingsTitle')}
      description={t('admin.productAlerts.settingsLead')}
      action={<HelpTip k="product_alerts.settings" />}
    >
      <fieldset disabled={!canManage} className="grid max-w-2xl gap-5">
        <legend className="sr-only">{t('admin.productAlerts.settingsTitle')}</legend>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Switch
              id="stock-alerts"
              checked={draft.stockAlerts}
              onCheckedChange={(v) => setDraft({ ...draft, stockAlerts: v })}
              disabled={!canManage}
            />
            <label htmlFor="stock-alerts" className="text-sm font-medium">
              {t('admin.productAlerts.stockToggle')}
            </label>
            <HelpTip k="product_alerts.stock_toggle" />
          </div>
          <p className="text-sm text-ink-3">{t('admin.productAlerts.stockToggleLead')}</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              id="price-drop"
              checked={pd.enabled}
              onCheckedChange={(v) => setPd({ enabled: v, storeId: pd.storeId ?? stores[0]?.id ?? null })}
              disabled={!canManage}
            />
            <label htmlFor="price-drop" className="text-sm font-medium">
              {t('admin.productAlerts.priceDropToggle')}
            </label>
            <HelpTip k="product_alerts.price_drop" />
          </div>
          <p className="text-sm text-ink-3">{t('admin.productAlerts.priceDropLead')}</p>
          {pd.enabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t('admin.productAlerts.store')}
                help={<HelpTip k="product_alerts.store" />}
                error={fieldError(save.error, 'priceDrop.storeId')}
              >
                {(p) => (
                  <Select
                    {...p}
                    value={pd.storeId ?? ''}
                    onChange={(e) => setPd({ storeId: e.target.value || null })}
                  >
                    <option value="">{t('admin.productAlerts.chooseStore')}</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field
                label={t('admin.productAlerts.minPercent')}
                help={<HelpTip k="product_alerts.min_percent" />}
              >
                {(p) => (
                  <Select
                    {...p}
                    value={pd.minPercent}
                    onChange={(e) => setPd({ minPercent: Number(e.target.value) })}
                  >
                    {PERCENTS.map((x) => (
                      <option key={x} value={x}>
                        {t('admin.productAlerts.percentOption', { pct: x })}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {turningOn ? (
              <ConfirmButton
                variant="primary"
                size="md"
                message={t('admin.productAlerts.enableConfirm')}
                onConfirm={() => save.mutateAsync(undefined).catch(() => undefined)}
                disabled={!dirty || save.isPending}
              >
                {t('admin.productAlerts.save')}
              </ConfirmButton>
            ) : (
              <Button
                variant="primary"
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || save.isPending}
              >
                {t('admin.productAlerts.save')}
              </Button>
            )}
            <HelpTip k="product_alerts.save" />
          </div>
        )}
      </fieldset>
    </Section>
  );
}

function RequestsDialog({
  row,
  onClose,
}: {
  row: StockAlertSummary['variants'][number];
  onClose: () => void;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState<'waiting' | 'notified' | 'cancelled'>('waiting');
  const q = useQuery({
    queryKey: ['stock-alert-requests', row.variantId, status],
    queryFn: () =>
      api.get<StockAlertRequest[]>(`/v1/admin/stock-alerts/variants/${row.variantId}?status=${status}`),
  });
  const cancel = useApiMutation((id: string) => api.post(`/v1/admin/stock-alerts/${id}/cancel`, {}), {
    invalidate: [['stock-alert-requests', row.variantId], ['stock-alerts']],
    success: t('admin.productAlerts.cancelled'),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={row.productTitle}
      description={[row.variantTitle, row.sku].filter(Boolean).join(' · ')}
      closeLabel={t('common.close')}
    >
      <div className="space-y-4 text-sm">
        <Field label={t('admin.productAlerts.statusFilter')} className="max-w-xs">
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              {(['waiting', 'notified', 'cancelled'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`admin.productAlerts.statuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {!q.data ? (
          <Spinner label={t('common.loading')} />
        ) : !q.data.length ? (
          <p className="text-ink-2">{t('admin.productAlerts.noRequests')}</p>
        ) : (
          <ol
            className="divide-y divide-line rounded-md border border-line"
            aria-label={t('admin.productAlerts.requestsTitle')}
          >
            {q.data.map((r, i) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                {r.position && <span className="w-8 tabular-nums text-ink-3">{r.position}.</span>}
                <span className="font-medium">{r.email}</span>
                {r.member && <Badge tone="pine">{t('admin.productAlerts.member')}</Badge>}
                <span className="text-ink-3">
                  {r.status === 'notified' && r.notifiedAt
                    ? t('admin.productAlerts.notifiedAt', { date: formatDate(r.notifiedAt) })
                    : r.status === 'cancelled'
                      ? t(`admin.productAlerts.reasons.${r.reason ?? 'panel'}`)
                      : t('admin.productAlerts.requestedAt', { date: formatDate(r.createdAt) })}
                </span>
                {r.status === 'waiting' && can('automation.manage') && (
                  <span className="ms-auto inline-flex items-center gap-1">
                    <ConfirmButton
                      size="sm"
                      variant="secondary"
                      message={t('admin.productAlerts.cancelConfirm')}
                      onConfirm={() => cancel.mutateAsync(r.id)}
                    >
                      {t('admin.productAlerts.cancel')}
                    </ConfirmButton>
                    {i === 0 && <HelpTip k="product_alerts.cancel" />}
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </Dialog>
  );
}
